import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FestigkeitStufe } from './festigkeit-stufen';
import { LAGERUNGSDICHTE_FARBEN, LAGERUNGSDICHTE_STUFEN, LagerungsdichteStufe, festigkeitenZurStufe } from './lagerungsdichte-stufen';
import { dichteWirkungen, zahlText, DichteWirkung } from './lagerungsdichte-wirkung';
import { getBodenartenTabelle } from '../evaluators/nwsk';

/**
 * Hilfe zur Lagerungsdichte einer Substratlage.
 *
 * Vorher standen hier drei Wörter („1 gering, 2 mittel, 3 hoch") — man sah die
 * Stufen, aber weder woran man sie unterscheidet noch was sie bewirken.
 *
 * Jetzt beides, und beides belegt:
 *  - Die Ansprache läuft über die FESTIGKEIT. Anlage 2 (Leistungsbeschreibung
 *    M-V), Abschnitt 3.3.2, ordnet sie ausdrücklich zu: 1 = f0 und f1, 2 = f2,
 *    3 = f3 und f4; die Stufen selbst stehen in SEA 95, Teil B, Abschnitt
 *    3.2.8.11 (S. 76 f.).
 *  - Die Wirkung wird aus der Bodenartentabelle GELESEN, nicht abgeschrieben.
 */
@Component({
	standalone: true,
	imports: [CommonModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Lagerungsdichte</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<p class="einleitung">
				Die Stufe sagt, wie dicht die Lage gepackt ist. Angesprochen wird sie über die
				<strong>Festigkeit</strong> — den Widerstand, den die Lage in natürlicher Lagerung
				dem Spaten, dem Häkchen und dem Taschenmesser entgegensetzt.
			</p>

			<table class="table table-sm stufen-tabelle">
				<thead>
					<tr>
						<th class="stufe-spalte">Stufe</th>
						<th class="f-spalte">Festigkeit</th>
						<th>Ansprache im Gelände</th>
					</tr>
				</thead>
				<tbody>
					<ng-container *ngFor="let z of zeilen">
						<tr *ngFor="let f of z.festigkeiten; let erste = first" [class.stufen-start]="erste">
							<td *ngIf="erste" [attr.rowspan]="z.festigkeiten.length" class="stufe-spalte"
								[style.background]="farbe(z.stufe)">
								<span class="stufe-wert">{{ z.stufe.wert }}</span>
								<span class="stufe-name">{{ z.stufe.name }}</span>
							</td>
							<td class="f-spalte">
								<code>{{ f.code }}</code>
								<span class="f-name">{{ f.name }}</span>
							</td>
							<td>
								{{ f.ansprache }}
								<span class="beispiel" *ngIf="f.beispiel">z. B. {{ f.beispiel }}</span>
							</td>
						</tr>
					</ng-container>
				</tbody>
			</table>

			<p class="hinweis">
				Die Ansprache hängt vom Feuchtezustand ab: am erdfrischen Profil ansprechen und
				später am ausgetrockneten Profil ergänzen.
			</p>

			<div class="block">
				<h6>Was die Stufe bewirkt</h6>
				<p class="block-text">
					Je dichter der Boden liegt, desto weniger Wasser hält er. Nutzbarer
					Wasserspeicher in <strong>mm je dm Mächtigkeit</strong>, aus der
					Bodenartentabelle. Bodenarten mit gleichen Werten stehen in einer Zeile.
				</p>
				<table class="table table-sm wirkung-tabelle">
					<thead>
						<tr>
							<th class="zahl">1 gering</th>
							<th class="zahl">2 mittel</th>
							<th class="zahl">3 hoch</th>
							<th>Bodenarten</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let w of wirkungen" [class.ohne-wirkung]="w.ohneWirkung">
							<td class="zahl">{{ text(w.gering) }}</td>
							<td class="zahl">{{ text(w.mittel) }}</td>
							<td class="zahl">{{ text(w.hoch) }}</td>
							<td class="arten">
								{{ w.bodenarten.join(', ') }}
								<span class="ohne-hinweis" *ngIf="w.ohneWirkung">Stufe ohne Wirkung</span>
							</td>
						</tr>
					</tbody>
				</table>
				<p class="block-fuss mb-0">
					Die Werte stammen aus der Liste „Bodenarten" und lassen sich in der
					Listenverwaltung ändern.
				</p>
			</div>

			<p class="hinweis">
				Bei Moorpunkten wird die Spalte nicht erhoben; gerechnet wird dort immer mit
				Stufe 1 (gering).
			</p>

			<p class="quelle">
				Zuordnung nach „Leistungsbeschreibung zur Erhebung von Standortsdaten in M-V"
				(Anlage 2), Abschnitt 3.3.2; Festigkeitsstufen nach SEA 95, Teil B,
				Abschnitt 3.2.8.11, S. 76 f. Werte des Wasserspeichers nach Konopatzky 1998.
			</p>
		</div>
	`,
	styles: [`
		.modal-body {
			padding: 1rem 1.5rem;
		}
		.einleitung {
			margin-bottom: 1rem;
		}
		.stufen-tabelle {
			margin-bottom: 0.75rem;
		}
		.stufen-tabelle th {
			background: #f8f9fa;
			font-weight: 600;
			white-space: nowrap;
		}
		.stufen-tabelle td {
			vertical-align: top;
		}
		.stufen-start td {
			border-top: 2px solid #adb5bd;
		}
		.stufe-spalte {
			width: 7rem;
			vertical-align: middle !important;
		}
		.stufe-wert {
			display: block;
			font-size: 1.4rem;
			font-weight: 600;
			line-height: 1.1;
		}
		.stufe-name {
			color: #495057;
		}
		.f-spalte {
			width: 9.5rem;
			white-space: nowrap;
		}
		.stufen-tabelle code {
			font-size: 0.95rem;
			color: #212529;
			background: #f1f3f5;
			border-radius: 3px;
			padding: 0.1rem 0.4rem;
			margin-right: 0.4rem;
		}
		.f-name {
			font-weight: 600;
			white-space: normal;
		}
		.beispiel {
			display: block;
			color: #6c757d;
			font-size: 0.82rem;
		}
		.hinweis {
			font-size: 0.9rem;
			margin-bottom: 1rem;
		}
		.block {
			border: 1px solid #dee2e6;
			border-radius: 4px;
			padding: 0.75rem 1rem;
			margin-bottom: 1rem;
			background: #f8f9fa;
		}
		.block h6 {
			font-weight: 600;
			margin-bottom: 0.5rem;
		}
		.block-text {
			font-size: 0.9rem;
			margin-bottom: 0.75rem;
		}
		.block-fuss {
			font-size: 0.82rem;
			color: #6c757d;
		}
		.wirkung-tabelle {
			background: #fff;
			margin-bottom: 0.75rem;
			font-size: 0.88rem;
		}
		.wirkung-tabelle td,
		.wirkung-tabelle th {
			vertical-align: top;
		}
		.wirkung-tabelle th {
			background: #f1f3f5;
			font-weight: 600;
			white-space: nowrap;
		}
		.zahl {
			width: 5.5rem;
			text-align: right;
			white-space: nowrap;
			padding-right: 1rem;
		}
		.arten {
			word-break: break-word;
		}
		.ohne-wirkung {
			color: #868e96;
		}
		/* Der Satz steht in der Zeile selbst. Nur eine blasse Schrift waere ein
		   Hinweis, den man erst sucht und dann uebersieht. */
		.ohne-hinweis {
			display: inline-block;
			margin-left: 0.5rem;
			padding: 0 0.5rem;
			font-size: 0.78rem;
			color: #6c757d;
			background: #fff;
			border: 1px solid #dee2e6;
			border-radius: 10px;
			white-space: nowrap;
		}
		.quelle {
			font-size: 0.82rem;
			color: #6c757d;
			margin-bottom: 0;
		}
	`]
})
export class LagerungsdichteInfoModalComponent {
	activeModal = inject(NgbActiveModal);

	zeilen: { stufe: LagerungsdichteStufe; festigkeiten: FestigkeitStufe[] }[] =
		LAGERUNGSDICHTE_STUFEN.map(stufe => ({ stufe, festigkeiten: festigkeitenZurStufe(stufe) }));

	/** Aus der Tabelle gelesen, mit der wirklich gerechnet wird. */
	wirkungen: DichteWirkung[] = dichteWirkungen(getBodenartenTabelle());

	text(wert: number): string {
		return zahlText(wert);
	}

	/** Dieselbe Farbe wie am Feld in der Substrattabelle (LAGERUNGSDICHTE_FARBEN). */
	farbe(stufe: LagerungsdichteStufe): string {
		return LAGERUNGSDICHTE_FARBEN[stufe.wert] ?? '#f8f9fa';
	}
}
